import { useState } from 'react'
import { message } from 'antd'
import {
  listAIVoices,
  generateAudio,
  generateDialogue,
  getTaskStatus
} from '../../../../../services/api/aiVideoGeneration'

export const useVoiceOver = ({ scenes, updateSceneData, videoId }) => {
  const [voices, setVoices] = useState([])
  const [loadingVoices, setLoadingVoices] = useState(false)

  const fetchVoices = async () => {
    setLoadingVoices(true)
    try {
      const res = await listAIVoices(videoId)
      const task_id = res?.data?.task_id
      const data = await waitForTaskSuccess(task_id)

      // Parse new TTS Enhanced API response format
      // Response structure: { default_voices: [...], uploaded_voices: [...] }
      const defaultVoices = data?.default_voices || []
      const uploadedVoices = data?.uploaded_voices || []

      // Combine both arrays and create a structured list
      const allVoices = [
        ...defaultVoices.map((voice, index) => ({
          id: voice,
          name: `Default ${index + 1}`,
          type: 'default'
        })),
        ...uploadedVoices.map(voice => ({
          id: voice,
          name: voice,
          type: 'uploaded'
        }))
      ]

      setVoices(allVoices)
    } catch (err) {
      console.error('Failed to fetch voices', err)
      message.error('Failed to load voices')
    } finally {
      setLoadingVoices(false)
    }
  }

  const waitForTaskSuccess = async taskId => {
    if (!taskId) throw new Error('Missing task id')

    const maxAttempts = 100 // 100 attempts * 3s = 5 minutes max
    const delayMs = 3000 // 3 seconds as per TTS Enhanced API documentation

    // Initial delay before first poll (2-3 seconds recommended)
    await new Promise(resolve => setTimeout(resolve, 2000))

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const statusResponse = await getTaskStatus(taskId)
        const data = statusResponse?.data || null
        const status = data?.status || null

        if (status === 'success') {
          const output = data?.output || data?.audio_url || data?.result || ''

          // Check if the operation itself failed (even though task status is success)
          if (output?.success === false || output?.error) {
            const errorMsg = output?.error || 'Audio generation failed'
            const err = new Error(errorMsg)
            err._isTerminal = true
            throw err
          }

          // Return the output object which contains audio_url or voice data
          return output
        }

        if (status === 'failed' || status === 'error') {
          const errorMsg = data?.output?.error || data?.error || 'Task failed'
          const err = new Error(errorMsg)
          err._isTerminal = true
          throw err
        }

        // Status is still 'pending' or 'processing', continue polling
      } catch (err) {
        if (err?._isTerminal === true || err.message === 'Task failed')
          throw err
        if (attempt === maxAttempts - 1) throw err
      }

      await new Promise(resolve => setTimeout(resolve, delayMs))
    }

    throw new Error('Task polling timed out')
  }

  // Helper function to calculate audio duration from URL
  const calculateAudioDuration = async audioUrl => {
    return new Promise((resolve, reject) => {
      const audio = new Audio(audioUrl)

      audio.addEventListener('loadedmetadata', () => {
        const duration = audio.duration
        // Round to 2 decimal places
        const roundedDuration = Math.round(duration * 100) / 100
        resolve(roundedDuration)
      })

      audio.addEventListener('error', e => {
        console.error('Error loading audio for duration calculation:', e)
        // Resolve with 0 instead of rejecting to prevent breaking the flow
        resolve(0)
      })

      // Set a timeout to prevent hanging
      setTimeout(() => {
        resolve(0)
      }, 10000) // 10 second timeout
    })
  }

  const generateVoiceForScene = async (sceneId, payload) => {
    // payload should be { voice_id, text, speed_factor?, exaggeration?, ... }

    // Ensure aggregated_voiceover is treated as string. If it's an object, default to ""
    const currentVoiceover = scenes.find(s => s.id === sceneId)
      ?.aggregated_voiceover
    const existingText =
      typeof currentVoiceover === 'string' ? currentVoiceover : ''

    try {
      // Update the scene's voiceover text immediately
      updateSceneData(sceneId, 'aggregated_voiceover', payload.text)

      const res = await generateAudio({ ...payload, video_id: videoId })
      const taskId = res?.data?.task_id
      const output = await waitForTaskSuccess(taskId)

      // Extract audio_url from the output object
      const audioUrl = output?.audio_url || output

      // Calculate audio duration
      const audioDuration = await calculateAudioDuration(audioUrl)

      // Update the scene's audio_link, audio_duration and segment_timings
      updateSceneData(sceneId, 'audio_link', audioUrl)
      updateSceneData(sceneId, 'audio_duration', audioDuration)
      if (output?.segment_timings) {
        updateSceneData(sceneId, 'segment_timings', output.segment_timings)
      }

      message.success('Voiceover generated')
      return audioUrl
    } catch (err) {
      console.error('Voice generation failed', err)
      // Do not write error object to aggregated_voiceover as per new requirement
      message.error('Voiceover generation failed')
      throw err
    }
  }

  const generateDialogueForScene = async (sceneId, payload) => {
    // payload should be { turns: [[voice_id, text], ...], silence_duration?, global_speed_factor? }
    const existing =
      scenes.find(s => s.id === sceneId)?.aggregated_voiceover || {}

    try {
      updateSceneData(sceneId, 'aggregated_voiceover', {
        ...existing,
        isGenerating: true,
        progress: 0,
        url: '',
        error: null,
        turns: payload.turns
      })

      const res = await generateDialogue({ ...payload, video_id: videoId })
      const taskId = res?.data?.task_id
      const output = await waitForTaskSuccess(taskId)

      // Extract audio_url from the output object
      const audioUrl = output?.audio_url || output

      // Calculate audio duration
      const audioDuration = await calculateAudioDuration(audioUrl)

      updateSceneData(sceneId, 'aggregated_voiceover', {
        ...existing,
        isGenerating: false,
        progress: 100,
        url: audioUrl,
        error: null,
        turns: payload.turns
      })

      // Update the scene's audio_link, audio_duration and segment_timings
      updateSceneData(sceneId, 'audio_link', audioUrl)
      updateSceneData(sceneId, 'audio_duration', audioDuration)
      if (output?.segment_timings) {
        updateSceneData(sceneId, 'segment_timings', output.segment_timings)
      }

      message.success('Dialogue generated')
      return audioUrl
    } catch (err) {
      console.error('Dialogue generation failed', err)
      updateSceneData(sceneId, 'aggregated_voiceover', {
        ...existing,
        isGenerating: false,
        progress: 0,
        url: '',
        error: { message: err?.message || 'Generation failed' }
      })
      message.error('Dialogue generation failed')
      throw err
    }
  }

  return {
    voices,
    loadingVoices,
    fetchVoices,
    generateVoiceForScene,
    generateDialogueForScene
  }
}
